/**
 * Canonical form-label primitive for the web app. Every "label-like"
 * piece of text in a form or settings UI should render through this
 * component (or the ``FormLabel`` wrapper in ``form.tsx``) — never as a
 * styled ``<p>`` or ``<span>``. Uniform usage means accessibility (real
 * ``<label htmlFor>`` association, screen-reader semantics, click-to-
 * focus) and consistent styling come for free.
 *
 * ## Usage patterns
 *
 * **Stacked input**: add a small bottom margin (or wrap label-and-input
 * in a ``space-y-*`` container) at the call site.
 * ```tsx
 * <Label htmlFor="my-input" className="mb-1.5">Phone number</Label>
 * <Input id="my-input" />
 * ```
 *
 * **Flex row with a Switch / inline control**:
 * ```tsx
 * <div className="flex items-center justify-between">
 *   <div className="space-y-1">
 *     <Label htmlFor="my-toggle">Enable thing</Label>
 *     <p className="text-muted-foreground text-xs">Helper text…</p>
 *   </div>
 *   <Switch id="my-toggle" />
 * </div>
 * ```
 *
 * **Inside a react-hook-form ``<Form>``**: use ``<FormLabel>`` from
 * ``form.tsx`` instead — it auto-binds ``htmlFor`` to the field id and
 * surfaces validation error styling.
 *
 * ## Anti-patterns to avoid
 *
 * - ``<p className="text-sm font-medium">…</p>`` to "look like" a label.
 *   Bypasses accessibility, drifts from shared styling, and doesn't
 *   participate in click-to-focus on the bound input.
 * - Hardcoding font / color overrides on each call site. If a design
 *   tweak is needed app-wide, change it here so every call site picks
 *   it up.
 *
 * ## Margin policy
 *
 * This primitive intentionally adds no default bottom margin. Layout
 * spacing belongs at the call site so we don't change every existing
 * label across the app when one form needs different breathing room.
 */
import * as LabelPrimitive from '@radix-ui/react-label';
import * as React from 'react';

import { cn } from '@apps/web/lib/utils';

function Label({
    className,
    ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
    return (
        <LabelPrimitive.Root
            data-slot="label"
            className={cn(
                'flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
                className,
            )}
            {...props}
        />
    );
}

export { Label };
